/**
 * The reorderable slot stack for one template.
 *
 * One flat list, one card per body element in body order, each named by its
 * slot's id, and every slot equally editable. A card is collapsed until tapped; exactly one is
 * expanded at a time and the rest dim, so the open card reads as the active
 * editing surface. Reordering is disabled while a card is open — the grab handle
 * belongs to the collapsed state.
 *
 * What a card looks like is never decided here: the collapsed card is a shell
 * around `SlotPreview` and the open one is `SlotEditor`, and both hand the slot
 * back to its own type through `SlotViews`.
 */

import React from 'react';
import {
  IonReorder,
  IonReorderGroup,
  type ItemReorderEventDetail,
} from '@ionic/react';
import {
  SlotLogic,
  type FormBuilderContext,
  type SlotType,
} from './slot_logic';
import type { SharingFormSlot } from './types';
import type { TopicTypeId } from '@/modules/topic/types';
import { SlotBadge, SlotPreview, typeClass } from './SlotViews';
import { LinkedNote, SlotEditor } from './SlotEditCard';
import type { SlotLinkProps } from './SlotEditors';
import styles from './slots.module.css';

/**
 * The slot types an author can add, in the order the add sheet lists them —
 * the order only. Each type names itself (`SlotLogic.typeName`) and says
 * whether it is on offer at all (`includeTypeInSlotInsertionOptions`).
 */
export const ADD_SLOT_TYPES: readonly SlotType[] = [
  'text',
  'select',
  'fillIn',
  'optional',
  'goal',
];

const CollapsedSlotCard: React.FC<{
  /** The whole stack, which the slot's variable warnings are judged against. */
  slots: readonly SharingFormSlot[];
  slot: SharingFormSlot;
  testId: string;
  context: FormBuilderContext;
  link?: SlotLinkProps;
  onOpen: () => void;
}> = ({ slots, slot, testId, context, link, onOpen }) => {
  const type = SlotLogic.typeOf(slot);
  const warning = SlotLogic.warningInStack(slots, slot.id, context.topicType);
  // A slot whose topic no longer supports it stays in the stack — still
  // editable, still deletable — and the stack's answer is to gray it out.
  // Whether it is still usable at all is the type's own to say.
  const usable = SlotLogic.isUsable(slot, context);
  return (
    <div className={styles.cardWithHandle}>
      <button
        type="button"
        className={`${styles.card} ${typeClass(type)} ${
          usable ? '' : styles.cardDisabled
        }`}
        onClick={onOpen}
        aria-label={SlotLogic.ariaLabel(slot)}
        data-testid={testId}
        data-slot-type={type}
      >
        <SlotBadge type={type} />
        <span className={styles.body}>
          <SlotPreview slot={slot} context={context} />
          {link && <LinkedNote linkedWith={link.linkedWith} testId={testId} />}
          {warning && (
            <span className={styles.warning} data-testid={`${testId}-warning`}>
              ⚠ {warning}
            </span>
          )}
        </span>
      </button>
      <IonReorder className={styles.handle} data-testid={`${testId}-handle`} />
    </div>
  );
};

export interface SlotStackProps {
  /** The template's whole body, in order — one flat list of slots. */
  slots: readonly SharingFormSlot[];
  /**
   * The id of the open card's slot, or null when no card in this stack is
   * open.
   */
  editingId: string | null;
  onEditingIdChange: (id: string | null) => void;
  onChange: (slots: readonly SharingFormSlot[]) => void;
  testIdPrefix: string;
  /**
   * Whether the topic (as drafted) still sets an impression goal. A goal slot
   * renders disabled without one — the slot is tied to that goal.
   */
  hasImpressionGoal?: boolean;
  /** Every variable the sharing setup assigns, for the variable picker. */
  variableNames?: readonly string[];
  /** The field id of the slot this form's allowed detail comes from, if any. */
  allowedDetailSlotId?: string | null;
  /** The topic's type, whose definitions the form may read. */
  topicType?: TopicTypeId;
  /**
   * The soft-link controls for the slot an id names, when the builder offers
   * linking across its forms; without it no card shows or offers links.
   */
  linkFor?: (id: string) => SlotLinkProps | undefined;
}

export const SlotStack: React.FC<SlotStackProps> = ({
  slots,
  editingId,
  onEditingIdChange,
  onChange,
  testIdPrefix,
  hasImpressionGoal = true,
  variableNames = [],
  allowedDetailSlotId = null,
  topicType,
  linkFor,
}) => {
  // One context for the whole stack: what the types are asked about the topic
  // on screen, whether they are answering a preview or a usability question.
  const openSlot =
    editingId === null ? undefined : SlotLogic.slotWithId(slots, editingId);
  const openLacksPrompt =
    openSlot !== undefined && SlotLogic.lacksPrompt(openSlot);

  const context: FormBuilderContext = {
    hasImpressionGoal,
    variableNames,
    allowedDetailSlotId,
    topicType,
  };

  /**
   * Commit a drag as an edit to the list, and nothing else.
   *
   * The stack renders straight from `slots`, so re-rendering in the new order
   * is the whole move. Completing the reorder the default way would ALSO have
   * Ionic carry the dragged element to its new place in the DOM, in an
   * animation frame that lands after React has already moved the cards (each
   * is keyed by its slot's id) — so the node would be moved twice, and an
   * adjacent swap would land exactly back where it started. `complete(false)`
   * leaves the DOM order to React and keeps only the drop animation.
   */
  const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
    event.detail.complete(false);
    onChange(SlotLogic.moveSlot(slots, event.detail.from, event.detail.to));
  };

  return (
    <div data-testid={testIdPrefix}>
      <IonReorderGroup
        disabled={openSlot !== undefined}
        onIonItemReorder={handleReorder}
      >
        {slots.map(slot => {
          const testId = `${testIdPrefix}-slot-${slot.id}`;
          const editing = editingId === slot.id;
          return (
            <div
              key={slot.id}
              className={`${styles.row} ${
                openSlot !== undefined && !editing ? styles.rowDimmed : ''
              }`}
            >
              {editing ? (
                <SlotEditor
                  slot={slot}
                  testId={`${testId}-editor`}
                  context={context}
                  link={linkFor?.(slot.id)}
                  onChange={next =>
                    onChange(SlotLogic.replaceSlot(slots, slot.id, next))
                  }
                  onCommit={() => onEditingIdChange(null)}
                  onDuplicate={() => {
                    onChange(SlotLogic.duplicateSlot(slots, slot.id));
                    onEditingIdChange(null);
                  }}
                  onDelete={() => {
                    onChange(SlotLogic.removeSlot(slots, slot.id));
                    onEditingIdChange(null);
                  }}
                />
              ) : (
                <CollapsedSlotCard
                  slots={slots}
                  slot={slot}
                  testId={testId}
                  context={context}
                  link={linkFor?.(slot.id)}
                  // Opening another card commits the one already open, which
                  // a Select or Fill in slot with a blank prompt can't do.
                  onOpen={() => {
                    if (openLacksPrompt) return;
                    onEditingIdChange(slot.id);
                  }}
                />
              )}
            </div>
          );
        })}
      </IonReorderGroup>
    </div>
  );
};
